<template>
    <v-container>
        <v-row>
            <v-col>
                <h1>Paid Time-Off and Leave</h1>
                <p>Please note that this information may not accurately reflect the time off you have earned or have available to use. This is due to delayed input of days, time taken off for disability, worker's compensation, FMLA, or unpaid leave in the current academic year. <strong>If you have any questions or edits, please contact <a href="https://hr.adelphi.edu/contact/" target="_blank">Human Resources.</a></strong></p>

                <div v-if="site_status=='saas_locked'">
                    <v-alert
                        border="top"
                        colored-border
                        type="info"
                        elevation="2"
                    >
                        During our PTO reconciliation process you will not be able to request future leave until our anticipated completion date of September 16, 2022. <br /><br />
                        We apologize for the inconvenience but please note this does not mean you can't take leave during this time. We ask that you communicate with your manager or whomever traditionally approves your leave to seek approval. <br /><br />
                        Thank you for your patience. <br />
                    </v-alert>
                </div>

                <div v-else>

                <p>Fiscal Year Begins: {{ this.pto_data.FiscalYearBegins | formatDate }}</p>
                <p>Fiscal Year Ends: {{ this.pto_data.FiscalYearEnds | formatDate }}</p>

                <v-simple-table>
                    <thead>
                        <tr>
                            <th colspan="2">Vacation Days</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Limit</td>
                            <td align="right">{{ this.pto_data.VacationLimit }}</td>
                        </tr>
                        <tr>
                            <td>Remaining</td>
                            <td align="right">{{ this.pto_data.VacationRemaining }}</td>
                        </tr>
                        <tr>
                            <td>Accrued</td>
                            <td align="right">{{ this.pto_data.VacationAccrued }}</td>
                        </tr>
                        <tr>
                            <td>Dates Taken</td>                                
                            <td align="right">
                                <div v-for="(item) in this.vacation_dates" :key="item.date">
                                    {{ item.date | formatDate }} - {{ item.day }} 
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>

                <v-simple-table>
                    <thead>
                        <tr>
                            <th colspan="2">Personal Days</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Limit</td>
                            <td align="right">{{ this.pto_data.PersonalLimit }}</td>
                        </tr>
                        <tr>
                            <td>Remaining</td>
                            <td align="right">{{ this.pto_data.PersonalRemaining }}</td>
                        </tr>
                        <tr>
                            <td>Accrued</td>
                            <td align="right">{{ this.pto_data.PersonalAccrued }}</td>
                        </tr>
                        <tr>
                            <td>Dates Taken</td>
                            <td align="right">
                                <div v-for="(item) in this.personal_dates" :key="item.date">
                                    {{ item.date | formatDate }} - {{ item.day }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>

                <v-simple-table>
                    <thead>
                        <tr>
                            <th colspan="2">Sick Days</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Limit</td>
                            <td align="right">{{ this.pto_data.SickLimit }}</td>
                        </tr>
                        <tr>
                            <td>Remaining</td>
                            <td align="right">{{ this.pto_data.SickRemaining }}</td>
                        </tr>
                        <tr>
                            <td>Carried Over</td>
                            <td align="right">{{ this.pto_data.SickCarryOver }}</td>
                        </tr>
                        <tr>
                            <td>Dates Taken</td>
                            <td align="right">
                                <div v-for="(item) in this.sick_dates" :key="item.date">
                                    {{ item.date | formatDate }} - {{ item.day }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>

                 <v-simple-table>
                    <thead>
                        <tr>
                            <th colspan="2">Holiday Days</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Limit</td>
                            <td align="right">{{ this.pto_data.HolidayLimit }}</td>
                        </tr>
                        <tr>
                            <td>Remaining</td>
                            <td align="right">{{ this.pto_data.HolidayRemaining }}</td>
                        </tr>
                        <tr>
                            <td>Dates Taken</td>
                            <td align="right">
                                <div v-for="(item) in this.holiday_dates" :key="item.date">
                                    {{ item.date | formatDate }} - {{ item.day }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>

                <v-simple-table v-if="this.pto_data.OtherDates_MV && this.pto_data.OtherDates_MV.OtherDates && this.pto_data.OtherDates_MV.OtherDates != null">
                    <thead>
                        <tr>
                            <th colspan="2">Other Days</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Limit</td>
                            <td align="right">{{ this.pto_data.OtherLimit }}</td>
                        </tr>
                        <tr>
                            <td>Remaining</td>
                            <td align="right">{{ this.pto_data.OtherRemaining }}</td>
                        </tr>
                        <tr>
                            <td>Dates Taken</td>
                            <td align="right">
                                <div v-for="(item) in this.other_dates" :key="item">
                                    {{ item.date | formatDate }} - {{ item.day }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>

                <v-container v-if="this.staff_pto_data !== null">
                <h3>Your Team's Paid Time Off and Leave</h3>
                <p>Please select the name of your direct-reports to view their time off report.</p>
                <v-expansion-panels focusable="true">
                    <v-expansion-panel v-for="item in this.staff_pto_data" :key="item">
                        <v-expansion-panel-header>{{ item.Legal_Name }}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-simple-table>
                                <thead>
                                    <tr>
                                        <th colspan="2">Vacation Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Limit</td>
                                        <td align="right">{{ item.VacationLimit }}</td>
                                    </tr>
                                    <tr>
                                        <td>Remaining</td>
                                        <td align="right">{{ item.VacationRemaining }}</td>
                                    </tr>
                                    <tr>
                                        <td>Dates Taken</td>
                                        <td v-if="Array.isArray(item.VacationDates_MV.VacationDates)" align="right" :set="newVacation = item.VacationDates_MV.VacationDates.map((date, i) => {
                                                return {'date': date, 'day': item.VacationDays_MV.VacationDays[i]}
                                            })">
                                            <div v-for="date in newVacation" :key="date">
                                                {{ date.date | formatDate }} - {{ date.day }}<br>
                                            </div>
                                        </td>
                                        <td v-else align="right">
                                            <div v-if="item.VacationDates_MV.VacationDates != null">
                                                {{ item.VacationDates_MV.VacationDates | formatDate }} - {{ item.VacationDays_MV.VacationDays }}<br>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>

                            <v-simple-table>
                                <thead>
                                    <tr>
                                        <th colspan="2">Personal Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Limit</td>
                                        <td align="right">{{ item.PersonalLimit }}</td>
                                    </tr>
                                    <tr>
                                        <td>Remaining</td>
                                        <td align="right">{{ item.PersonalRemaining }}</td>
                                    </tr>
                                    <tr>
                                        <td>Dates Taken</td>
                                        <td v-if="Array.isArray(item.PersonalDates_MV.PersonalDates)" align="right" :set="newPersonal = item.PersonalDates_MV.PersonalDates.map((date, i) => {
                                                return {'date': date, 'day': item.PersonalDays_MV.PersonalDays[i]}
                                            })">
                                             
                                            <div v-for="date in newPersonal" :key="date">
                                                {{ date.date | formatDate }} - {{ date.day }}<br>
                                            </div>
                                        </td>
                                        <td v-else align="right">
                                            <div v-if="item.PersonalDates_MV.PersonalDates != null">
                                                {{ item.PersonalDates_MV.PersonalDates | formatDate }} - {{ item.PersonalDays_MV.PersonaDays }}<br>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>

                            <v-simple-table>
                                <thead>
                                    <tr>
                                        <th colspan="2">Sick Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Limit</td>
                                        <td align="right">{{ item.SickLimit }}</td>
                                    </tr>
                                    <tr>
                                        <td>Remaining</td>
                                        <td align="right">{{ item.SickRemaining }}</td>
                                    </tr>
                                    <tr>
                                        <td>Carried Over</td>
                                        <td align="right">{{ item.SickCarryOver }}</td>
                                    </tr>
                                    <tr>
                                        <td>Dates Taken</td>
                                        <td v-if="Array.isArray(item.SickDates_MV.SickDates)" align="right" :set="newSick = item.SickDates_MV.SickDates.map((date, i) => {
                                                return {'date': date, 'day': item.SickDays_MV.SickDays[i]}
                                            })">
                                            
                                            <div v-for="date in newSick" :key="date">
                                                {{ date.date| formatDate }} - {{ date.day }}<br>
                                            </div>
                                        </td>
                                        <td v-else align="right">
                                            <div v-if="item.SickDates_MV.SickDates != null">
                                                {{ item.SickDates_MV.SickDates | formatDate }} - {{ item.SickDays_MV.SickDays }}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>

                            <v-simple-table>
                                <thead>
                                    <tr>
                                        <th colspan="2">Holiday Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Limit</td>
                                        <td align="right">{{ item.HolidayLimit }}</td>
                                    </tr>
                                    <tr>
                                        <td>Remaining</td>
                                        <td align="right">{{ item.HolidayRemaining }}</td>
                                    </tr>
                                    <tr>
                                        <td>Dates Taken</td>
                                        <td v-if="Array.isArray(item.HolidayDates_MV.HolidayDates)" align="right" :set="newHoliday = item.HolidayDates_MV.HolidayDates.map((date, i) => {
                                                return {'date': date, 'day': item.HolidayDays_MV.HolidayDays[i]}
                                            })">
                                            <div v-for="date in newHoliday" :key="date">
                                                {{ date | formatDate }}<br>
                                            </div>
                                        </td>
                                        <td v-else align="right">
                                            <div v-if="item.HolidayDates_MV.HolidayDates != null">
                                                {{ item.HolidayDates_MV.HolidayDates | formatDate }} - {{ item.HolidayDays_MV.HolidayDays }}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>

                            <v-simple-table v-if="item.OtherDates_MV.OtherDates != null">
                                <thead>
                                    <tr>
                                        <th colspan="2">Other Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Limit</td>
                                        <td align="right">{{ item.OtherLimit }}</td>
                                    </tr>
                                    <tr>
                                        <td>Remaining</td>
                                        <td align="right">{{ item.OtherRemaining }}</td>
                                    </tr>
                                    <tr>
                                        <td>Dates Taken</td>
                                        <td v-if="Array.isArray(item.OtherDates_MV.OtherDates)" align="right" :set="newOther = item.OtherDates_MV.OtherDates.map((date, i) => {
                                                return {'date': date, 'day': item.OtherDays_MV.OtherDays[i]}
                                            })">
                                            <div v-for="date in item.OtherDates_MV.OtherDates" :key="date">
                                                {{ date.date | formatDate }} - {{ date.day }<br>
                                            </div>
                                        </td>
                                        <td v-else align="right">
                                            <div v-if="item.OtherDates_MV.OtherDates != null">
                                                {{ item.OtherDates_MV.OtherDates | formatDate }} - {{ item.OtherDays_MV.OtherDays }}<br>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>

                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
                </v-container>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
import axios from "axios";
import pto_func from "@/pto_functions";

export default {
    
    data:() => ({
        pto_data: [null],
        staff_pto_data: [],
        vacation_dates: [],
        personal_dates: [],
        sick_dates: [],
        sick_days: [],
        holiday_dates: [],
        other_dates: [],
        staff_members: [],
        site_status: pto_func.get_site_status(),

    }),
    mounted() {
        axios.get('./remote-apis/user-api.php?cmd=pto')
            .then(response => {
                this.pto_data = response.data.ROOT.Directory_SS
                
                if (Array.isArray(this.pto_data.VacationDates_MV.VacationDates)) {
                    for (let i = 0; i < this.pto_data.VacationDates_MV.VacationDates.length; i++) {
                        this.vacation_dates.push({"date": this.pto_data.VacationDates_MV.VacationDates[i], "day": this.pto_data.VacationDays_MV.VacationDays[i]})
                    }
                }else {
                    this.vacation_dates.push({"date": this.pto_data.VacationDates_MV.VacationDates, "day": this.pto_data.VacationDays_MV.VacationDays})
                }
                
                if (Array.isArray(this.pto_data.PersonalDates_MV.PersonalDates)) {
                    for (let i = 0; i < this.pto_data.PersonalDates_MV.PersonalDates.length; i++) {
                        this.personal_dates.push({"date": this.pto_data.PersonalDates_MV.PersonalDates[i], "day": this.pto_data.PersonalDays_MV.PersonalDays[i]})
                    }
                }else {
                    this.personal_dates.push({"date": this.pto_data.PersonalDates_MV.PersonalDates, "day": this.pto_data.PersonalDays_MV.PersonalDays})
                }

                if (Array.isArray(this.pto_data.SickDates_MV.SickDates)) {
                    for (let i = 0; i < this.pto_data.SickDates_MV.SickDates.length; i++) {
                        this.sick_dates.push({"date": this.pto_data.SickDates_MV.SickDates[i], "day": this.pto_data.SickDays_MV.SickDays[i]})
                    }
                }else {
                    this.sick_dates.push({"date": this.pto_data.SickDates_MV.SickDates, "day": this.pto_data.SickDays_MV.SickDays})
                }


                if (Array.isArray(this.pto_data.HolidayDates_MV.HolidayDates)) {
                    for (let i = 0; i < this.pto_data.HolidayDates_MV.HolidayDates.length; i++) {
                        this.holiday_dates.push({"date": this.pto_data.HolidayDates_MV.HolidayDates[i], "day": this.pto_data.HolidayDays_MV.HolidayDays[i]})
                    }
                }else {
                    this.holiday_dates.push({"date": this.pto_data.HolidayDates_MV.HolidayDates, "day": this.pto_data.HolidayDays_MV.HolidayDays})
                }

                if (Array.isArray(this.pto_data.OtherDates_MV.OtherDates)) {
                    for (let i = 0; i < this.pto_data.OtherDates_MV.OtherDates.length; i++) {
                        this.other_dates.push({"date": this.pto_data.OtherDates_MV.OtherDates[i], "day": this.pto_data.OtherDays_MV.OtherDays[i]})
                    }
                }else {
                    this.other_dates.push({"date": this.pto_data.OtherDates_MV.OtherDates, "day": this.pto_data.OtherDays_MV.OtherDays})
                }
                
            })
        
        axios.get('./remote-apis/user-api.php?cmd=pto-staff')
            .then(response => {
                //this.staff_pto_data = response.data.ROOT.Directory_SS
                if (response && response.data && response.data.ROOT == null) {
                    // do nothing
                } else if (Array.isArray(response.data.ROOT.Directory_SS)) {
                    for (let i = 0; i < response.data.ROOT.Directory_SS.length; i++) {
                        this.staff_pto_data.push(response.data.ROOT.Directory_SS[i])
                    }
                }else {
                    this.staff_pto_data.push(response.data.ROOT.Directory_SS)
                }
                
                for (let i = 0; i < this.staff_pto_data.length; i++) {
                    this.staff_pto_data[i].personal_days = []
                    this.staff_pto_data[i].personal_days.push({"date": this.staff_pto_data[i].PersonalDates_MV.PersonalDates, "day": this.staff_pto_data.PersonalDays_MV.PersonalDates}) 
                }
                
            })
    }
}
</script>